/**
 * MonitoringTabs - Horizontal tab container for monitoring detail view
 * Replaces the sidebar tab layout with horizontal tabs at top
 */
import { FC } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@pagespeedify/ui';
import { LayoutDashboard, BarChart3, Lightbulb, History } from 'lucide-react';
import { MonitoredUrl, Insight, MetricStats, FlattenedMonitoringHistory } from '../../types';
import { OverviewTab } from './tabs/OverviewTab';
import { AnalyticsTab } from './tabs/AnalyticsTab';
import { InsightsTab } from './tabs/InsightsTab';
import { HistoryTab } from './tabs/HistoryTab';
import type { UserType } from '../../lib/auth/types';

interface MonitoringTabsProps {
    history: FlattenedMonitoringHistory[];
    fieldDataHistory: any[]; // Field data history from additional_metrics.field_data_history
    selectedUrl: MonitoredUrl | null;
    insights: Insight[];
    stats: Record<string, MetricStats>;
    monitoringUsage?: any;
    user?: { uid: string; email: string | null; user_type?: UserType } | null;
}

export const MonitoringTabs: FC<MonitoringTabsProps> = ({
    history,
    fieldDataHistory,
    selectedUrl,
    insights,
    stats,
    monitoringUsage,
    user,
}) => {
    //Calculate total high-severity count including field data insights
    const backendHighCount = insights.filter(i => i.severity === 'high').length;
    const fieldHighCount = (fieldDataHistory && fieldDataHistory.length > 0) ?
        // Count field insights that would be created (bootup-time, render-blocking, unused-js/css with >100KB/50KB)
        (fieldDataHistory[0]?.audits?.['bootup-time']?.details?.items?.filter((item: any) => item.scripting > 100).length > 0 ? 1 : 0) +
        (fieldDataHistory[0]?.audits?.['render-blocking-resources']?.details?.items?.length > 0 ? 1 : 0) : 0;
    const totalHighInsights = backendHighCount + fieldHighCount;

    return (
        <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full flex justify-start gap-1 bg-muted/50 p-1 rounded-lg mb-6">
                <TabsTrigger
                    value="overview"
                    className="flex items-center gap-2 px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden sm:inline">Overview</span>
                </TabsTrigger>
                <TabsTrigger
                    value="analytics"
                    className="flex items-center gap-2 px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Analytics</span>
                </TabsTrigger>
                <TabsTrigger
                    value="insights"
                    className="flex items-center gap-2 px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                    <Lightbulb className="h-4 w-4" />
                    <span className="hidden sm:inline">Insights</span>
                    {totalHighInsights > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-full">
                            {totalHighInsights}
                        </span>
                    )}
                </TabsTrigger>
                <TabsTrigger
                    value="history"
                    className="flex items-center gap-2 px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                    <History className="h-4 w-4" />
                    <span className="hidden sm:inline">History</span>
                </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0">
                <OverviewTab
                    history={history}
                    fieldDataHistory={fieldDataHistory}
                    stats={stats}
                    selectedUrl={selectedUrl}
                />
            </TabsContent>

            <TabsContent value="analytics" className="mt-0">
                <AnalyticsTab
                    history={history}
                    fieldDataHistory={fieldDataHistory}
                    stats={stats}
                    monitoringUsage={monitoringUsage}
                    user={user}
                />
            </TabsContent>

            <TabsContent value="insights" className="mt-0">
                <InsightsTab
                    insights={insights}
                    history={history}
                    fieldDataHistory={fieldDataHistory}
                />
            </TabsContent>

            <TabsContent value="history" className="mt-0">
                <HistoryTab
                    history={history}
                    fieldDataHistory={fieldDataHistory}
                    selectedUrl={selectedUrl}
                    monitoringUsage={monitoringUsage}
                    user={user}
                />
            </TabsContent>
        </Tabs>
    );
};

export default MonitoringTabs;
